import { initSwiper } from '../components/swiper';
import initCartRecs from '../components/cart-recs';
import changeCart from './changeCart';
import { debounce } from 'throttle-debounce';
import { SETTINGS, SWATCHES } from '../utils/constants';
import getLocalePrefix from './getLocalePrefix';
import initMinicartDeliveryMessage from './minicart-delivery';
import initMinicartOrderNote from './minicart-order-note';

const fetchCartSwatchData = async () => {
  const response = await fetch(`${window.Shopify.routes.root}cart.js`, { cache: 'no-store' });
  if (!response.ok) return null;
  const cart = await response.json();
  const swatchItems = cart.items.filter((item) => item.properties?.[SWATCHES.PROPERTY_KEY] === 'true');
  const totalSwatches = swatchItems.reduce((sum, item) => sum + item.quantity, 0);
  return { swatchItems, totalSwatches };
};

const updateMinicartSwatchIncrementState = async () => {
  try {
    const data = await fetchCartSwatchData();
    if (!data) return;
    const atLimit = data.totalSwatches >= SWATCHES.MAX_QUANTITY;
    data.swatchItems.forEach((item) => {
      const lineEl = document.querySelector(`[data-test-id="cart-item-${item.key}"]`);
      const incrementBtn = lineEl?.querySelector('[data-minicart-qty-button="increment"]');
      if (incrementBtn) incrementBtn.disabled = atLimit;
    });
  } catch (error) {
    console.warn('Could not update swatch increment state:', error);
  }
};

const renderMinicart = async () => {
  const minicartLineItemSelector = '[data-minicart-line-item]';

  const getMinicartData = async () => {
    const localePrefix = getLocalePrefix();
    const url = `${window.location.origin}${localePrefix}/cart?view=minicart`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      return await res.text();
    } catch (e) {
      console.error('Failed to fetch minicart:', e);
      return null;
    }
  };

  const reinitMinicartUpsell = () => {
    if (!window.theme.swiperObjects) window.theme.swiperObjects = [];

    const sliderEl = document.querySelector('[data-minicart-upsell-swiper]');
    if (!sliderEl) return;

    const existingSwiperIndex = window.theme.swiperObjects.findIndex(swiper => swiper?.el === sliderEl);

    if (existingSwiperIndex !== -1) {
      const existingSwiper = window.theme.swiperObjects[existingSwiperIndex];
      if (existingSwiper && !existingSwiper.destroyed) {
        existingSwiper.destroy(true, true);
      }
      window.theme.swiperObjects.splice(existingSwiperIndex, 1);
    }

    const newSwiper = initSwiper(sliderEl);
    window.theme.swiperObjects.push(newSwiper);
  };

  const setLineItemLoadingState = (el) => {
    el?.classList.add('opacity-50');
  };

  const bindMinicartEvents = (renderMinicartFn) => {
    const minicart = document.querySelector('[data-minicart]');
    if (!minicart) return;

    if (minicart.dataset.eventsBound === 'true') return;
    minicart.dataset.eventsBound = 'true';

    minicart.addEventListener('click', async (evt) => {
      const btn = evt.target.closest('[data-minicart-remove-item]');
      if (btn) {
        evt.preventDefault();
        btn.classList.add('pointer-events-none');
        const lineItem = btn.closest(minicartLineItemSelector);
        setLineItemLoadingState(lineItem);
        await changeCart(btn.dataset.minicartRemoveItem, 0);
        await renderMinicartFn();
        return;
      }

      const qtyBtn = evt.target.closest('[data-minicart-qty-button]');
      if (!qtyBtn) return;

      const lineItem = qtyBtn.closest(minicartLineItemSelector);
      const input = lineItem?.querySelector('[data-minicart-qty]');
      if (!input) return;

      if (qtyBtn.dataset.minicartQtyButton === 'increment') {
        try {
          const data = await fetchCartSwatchData();
          if (data) {
            const isSwatchItem = data.swatchItems.some((item) => item.key === input.dataset.minicartQty);
            if (isSwatchItem && data.totalSwatches >= SWATCHES.MAX_QUANTITY) return;
          }
        } catch (error) {
          console.warn('Could not verify swatch limit before increment:', error);
        }
      }

      const currentValue = Number(input.value) || 1;
      const nextValue = qtyBtn.dataset.minicartQtyButton === 'increment'
        ? currentValue + 1
        : Math.max(1, currentValue - 1);

      if (nextValue === currentValue) return;

      qtyBtn.classList.add('pointer-events-none');
      input.value = String(nextValue);
      setLineItemLoadingState(lineItem);
      await changeCart(input.dataset.minicartQty, nextValue);
      await renderMinicartFn();
    });

    minicart.addEventListener('change', debounce(SETTINGS.MINICART_DEBOUNCE_DELAY, (evt) => {
      (async () => {
        const input = evt.target.closest('[data-minicart-qty]');
        if (!input) return;

        const lineItem = input.closest(minicartLineItemSelector);
        const lineItemKey = input.dataset.minicartQty;

        try {
          const data = await fetchCartSwatchData();
          if (data) {
            const thisItem = data.swatchItems.find((item) => item.key === lineItemKey);
            if (thisItem) {
              const otherQty = data.totalSwatches - thisItem.quantity;
              input.value = String(Math.min(+input.value, Math.max(1, SWATCHES.MAX_QUANTITY - otherQty)));
            }
          }
        } catch (error) {
          console.warn('Could not verify swatch limit before change:', error);
        }

        setLineItemLoadingState(lineItem);
        await changeCart(lineItemKey, +input.value);
        await renderMinicartFn();
      })();
    })
    );
  };

  const minicartContent = document.querySelector('[data-minicart-content]');
  if (!minicartContent) return;

  const htmlText = await getMinicartData();
  if (!htmlText) return;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlText, 'text/html');
  const minicartNewData = doc.querySelector('[data-minicart-render]');
  if (!minicartNewData) return;

  // Preserve any in-progress order note text before the markup is replaced,
  // so a quantity change mid-note doesn't discard what the customer typed.
  const previousNote = minicartContent.querySelector('[data-cart-order-note-input]')?.value;

  minicartContent.innerHTML = minicartNewData.innerHTML;

  reinitMinicartUpsell();
  initMinicartDeliveryMessage();
  initMinicartOrderNote(previousNote);
  initCartRecs();
  bindMinicartEvents(renderMinicart);
  await updateMinicartSwatchIncrementState();
};

export default renderMinicart;
